import {useMemo} from 'react'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useQueries, useQueryClient, type Query, type QueryClient} from '@github-ui/react-query'

import {copilotChatRepositoryPath} from '../utils/api-url-helpers'

export interface RepoInfo {
  id: number
  name: string
  ownerLogin: string
  description?: string | null
  visibility?: string
  primaryLanguage?: string
  /** Set only for incomplete repository data seeded from the SSR payload. */
  partial?: boolean
}

type SSRRepoInfo = Pick<RepoInfo, 'id' | 'name' | 'ownerLogin'>

interface UseRepoInfoOptions {
  requireFullDetails?: boolean
}

export function seedRepoInfoCache(queryClient: QueryClient, repositories: readonly SSRRepoInfo[]): void {
  for (const repo of repositories) {
    queryClient.setQueryData<RepoInfo | null>(['repo-info', repo.id], cachedRepo =>
      cachedRepo && cachedRepo.partial !== true ? {...cachedRepo, ...repo} : {...repo, partial: true},
    )
  }
}

async function fetchRepoInfo(repoID: number): Promise<RepoInfo | null> {
  try {
    const res = await verifiedFetchJSON(copilotChatRepositoryPath(repoID), {method: 'GET'})
    if (!res.ok) return null
    const payload = await res.json()
    const primaryLanguage = Array.isArray(payload.languages) ? payload.languages[0]?.name : undefined
    return {
      id: payload.id,
      name: payload.name,
      ownerLogin: payload.owner?.login ?? payload.ownerLogin,
      ...(payload.description === null || typeof payload.description === 'string'
        ? {description: payload.description}
        : {}),
      ...(typeof payload.visibility === 'string' && payload.visibility.length > 0
        ? {visibility: payload.visibility}
        : {}),
      ...(typeof primaryLanguage === 'string' && primaryLanguage.length > 0 ? {primaryLanguage} : {}),
    }
  } catch {
    return null
  }
}

/**
 * Hook to fetch repository info for a set of repository IDs.
 * Returns a map of repository ID to RepoInfo, loading state, and error state.
 */
export function useRepoInfo(repoIds: number[], {requireFullDetails = false}: UseRepoInfoOptions = {}) {
  const queryClient = useQueryClient()
  // Deduplicate and sort IDs for stable query keys
  const uniqueIds = useMemo(() => [...new Set(repoIds)].sort((a, b) => a - b), [repoIds])

  const queries = useQueries({
    queries: uniqueIds.map(repoId => {
      const queryKey = ['repo-info', repoId] as const
      return {
        queryKey,
        queryFn: async () => {
          const repoInfo = await fetchRepoInfo(repoId)
          return repoInfo ?? queryClient.getQueryData<RepoInfo | null>(queryKey) ?? null
        },
        staleTime: (query: Query<RepoInfo | null>) =>
          requireFullDetails && (query.state.data === null || query.state.data?.partial === true) ? 0 : Infinity,
      }
    }),
  })

  // Extract stable values from queries to use in dependencies
  const queryData = queries.map(q => q.data)

  // Build the repo map from query results
  const repoMap = useMemo(() => {
    const map: Record<number, RepoInfo> = {}
    for (const [index, data] of queryData.entries()) {
      const repoId = uniqueIds[index]
      if (data && repoId !== undefined) {
        map[repoId] = data
      }
    }
    return map
  }, [queryData, uniqueIds])

  const isLoading = queries.some(q => q.isLoading)
  const isFetching = queries.some(q => q.isFetching)
  const isError = queries.some(q => q.isError)

  return {data: repoMap, isLoading, isFetching, isError}
}
